import StateObserver, { user } from 'src/StateObserver';
import { openProfile } from 'src/game/profile/profile.sequence';
import { FriendVisitDetails } from 'src/replicant/visit';
import * as Visit from 'src/replicant/visit';
import FriendsMenu from '../FriendsMenu';
import { LockableFooterButton } from './LockableFooterButton';
import { getTutorialController } from 'src/game/tutorial/TutorialController';
import FooterFriendsBadge from './FooterFriendsBadge';
import { isPlatformGuest, withLoading } from 'src/lib/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import {
  getVisitDetails,
  startVisitSequence,
} from 'src/game/visit/visit.sequence';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { promptLINELoginNew } from 'src/game/guest/guest.sequence';
import { AvatarFrameId } from 'src/replicant/profile/frames';
import { isGuestUser } from 'src/replicant/guest/guest.getters';

export class FooterFriendsButton extends LockableFooterButton {
  private friendsMenu: FriendsMenu | undefined;

  constructor() {
    super({ type: 'friends' });

    new FooterFriendsBadge({
      superview: this,
      size: 45,
      x: this.style.width - 45,
      font: 'Body',
      fontSize: 22,
      textColor: 'white',
      style: 'green',
    });
  }

  public async setActive(isActive: boolean) {
    const { user, care } = StateObserver.getState();
    if (isActive && care.currentCareAction) {
      return;
    }

    super.setActive(isActive);

    if (isActive) {
      this.friendsMenu = new FriendsMenu();
      void getTutorialController().handleEvent('friendsMenuOpened');
      this.friendsMenu?.once('Close', () => this.onClick());
      this.friendsMenu?.fadeIn(this.getSuperview()!);

      // Show popup when referred friend unlocks visit.
      await friendUnlockedVisit();

      // Handle visit sequence unlocked.
      await visitUnlocked();
      return;
    }

    this.friendsMenu?.fadeOut();
  }

  public async onClick() {
    if (isGuestUser(user()) && isPlatformGuest(platform)) {
      await promptLINELoginNew('friendsMenu');
      return;
    }
    return super.onClick();
  }
}

async function visitUnlocked(user = StateObserver.getState().user) {
  // Prompt user to setup in-game profile if they haven't already,
  if (user.publicProfile) return;

  const canVisitFriends = Visit.getters.isUnlocked(user);

  // Should only prompt if user can visit friends.
  if (!canVisitFriends) return;

  // Start fetching the inviter's profile.
  const inviterDetails: Promise<FriendVisitDetails | null> = user.social
    .referral.invitedBy
    ? getVisitDetails(user.social.referral.invitedBy).get()
    : Promise.resolve(null);

  // Ensure user will create their public profile before visiting friends.
  if (!user.publicProfile) {
    await openProfile(undefined);
  }

  // Wait for data to be fetched and display popup.
  const visitDetails = await withLoading(() => inviterDetails, {
    feature: Feature.VISIT._,
    subFeature: Feature.VISIT.INVITER_DETAILS,
  });
  if (visitDetails) {
    const visit = await openPopupPromise('popupVisitReferral', {
      avatar: visitDetails.photo ?? '',
      avatarFrame: visitDetails.frame,
      playerName: visitDetails.name ?? '',
      title: i18n('social.invite.referral.letsGoOut'),
      message: i18n('social.invite.referral.visitReferral'),
    });
    if (visit) {
      await startVisitSequence({ friendId: visitDetails.id }, undefined);
    }
  }
}

async function friendUnlockedVisit(user = StateObserver.getState().user) {
  // Check if there's any referred friend that unlocked visit.
  const referred = user.social.referral.visitableFriend;
  if (!referred) return;

  // Remove from state.
  await StateObserver.invoke.clearReferredVisitable();

  // Prompt visit.
  const visit = await openPopupPromise('popupVisitReferral', {
    avatar: referred.photo ?? '',
    avatarFrame: (referred.frame ?? 'default') as AvatarFrameId,
    playerName: referred.name ?? '',
    title: i18n('social.invite.referral.letsGoOut'),
    message: i18n('social.invite.referral.visitReferred'),
  });
  if (visit) {
    await startVisitSequence(
      {
        friendId: referred.friendId,
      },
      undefined,
    );
  }
}
